import { createContext, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, signInWithGoogle, signInWithFacebook, signOut as supabaseSignOut, signIn, signUp } from '../lib/supabaseClient';
import { presenceService } from '@/services/presenceService';
import { profileService } from '@/services/profileService';
import { setExerciseCacheScope } from '@/services/exerciseService';

const PROFILE_CACHE_KEY = 'trenio_user_profile_cache';

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    signInWithGoogle: () => Promise<any>;
    signInWithFacebook: () => Promise<any>;
    signIn: typeof signIn;
    signUp: typeof signUp;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);

    // Fetch and cache user profile after authentication
    const fetchAndCacheProfile = async () => {
        try {
            const data = await profileService.getProfile();
            if (data) {
                localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
                    user: data.user,
                    profile: data.profile
                }));
                // Dispatch event to notify useUserProfile hook
                window.dispatchEvent(new Event('trenio_user_profile_updated'));
            }
        } catch (error) {
            console.error('[AuthContext] Failed to fetch profile:', error);
        }
    };

    useEffect(() => {
        // Check active sessions and sets the user
        supabase.auth.getSession().then(({ data: { session } }) => {
            setExerciseCacheScope(session?.user?.id);
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);

            // Fetch profile if session exists
            if (session?.user) {
                fetchAndCacheProfile();
                // On page reload Supabase emits INITIAL_SESSION (not SIGNED_IN), so without
                // this the user context would be missing from every error after a refresh.
                Sentry.setUser({ id: session.user.id, email: session.user.email });
            }
        });

        // Listen for changes on auth state (sign in, sign out, etc.)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            // Exercise pages include the account's private exercises (issue #236, F06)
            setExerciseCacheScope(session?.user?.id);
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);

            // Fetch profile on sign in (including OAuth)
            if (event === 'SIGNED_IN' && session?.user) {
                fetchAndCacheProfile();
                Sentry.setUser({ id: session.user.id, email: session.user.email });
            }

            // Clear cache on sign out
            if (event === 'SIGNED_OUT') {
                localStorage.removeItem(PROFILE_CACHE_KEY);
                Sentry.setUser(null);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Initialize presence tracking
    useEffect(() => {
        if (!user) return;

        const initPresence = async () => {
            await presenceService.initialize(user.id);
        };

        initPresence();

        // Cleanup on logout
        return () => {
            presenceService.cleanup();
        };
    }, [user]);

    const value = {
        user,
        session,
        loading,
        signInWithGoogle,
        signInWithFacebook,
        signIn,
        signUp,
        signOut: supabaseSignOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
